import { Component } from 'react';
import { withCookies } from 'react-cookie';
import { Helmet } from 'react-helmet';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';

// components
import AppErrorBoundary from './AppErrorBoundary';
import ErrorPage from '../error';
import FourOhFour from './FourOhFour';
import HoldersList from 'containers/HoldersList';
import HolderProfile from 'containers/HolderProfile';
import Intake from 'containers/Intake';
import Login from 'containers/Login';
import MobilePhotoUpload from 'components/intake/Account/components/MobilePhotoUpload';
import Referrals from 'containers/Referrals';
import Settings from 'containers/Settings';
import QuickLink from 'components/QuickLink';
import AdminHomepage from 'containers/AdminHomepage';
import OrgCollectionHomepage from 'containers/OrgCollectionHomepage';

import LogoutRoute from './LogoutRoute';
import PrivateRoute from './PrivateRoute';
import Routes from 'routes';

import AuthManager from '../../auth';
import { featureToggleContext } from '../_util_components/FeatureToggle';

import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';

import './App.scss';

import customTheme from '../_theme';
import { OrganizationCollection } from 'types/index';

interface AppProps {
    email: string;
    fetchUser: (userId: number) => void;
    getApiError: boolean;
    getApiErrorResponse: {
        message: string;
    };
    id: number;
    last_name: string;
    name: string;
    unauthorizedUser: boolean;
    is_admin?: boolean;
    organization_collections: OrganizationCollection[];
}

class App extends Component<AppProps> {
    static contextType = featureToggleContext;

    componentDidMount() {
        const { id } = this.props;
        const userId = AuthManager.getInstance().getUserID();

        if (!id && userId) {
            this.props.fetchUser(parseInt(userId));
        }
    }

    getHomeComponent = () => {
        const { is_admin, organization_collections } = this.props;
        if ( is_admin ) return AdminHomepage;
        if (organization_collections === undefined || organization_collections.length === 0) {
            return HoldersList;
        }
        return OrgCollectionHomepage;
    };

    componentDidUpdate(prevProps: AppProps) {
        if (this.props.unauthorizedUser && !prevProps.unauthorizedUser) {
            const auth = AuthManager.getInstance();
            auth.unsetToken();
        }
    }

    render() {
        const {
            getApiError,
            getApiErrorResponse,
            unauthorizedUser,
            email,
            name,
            last_name
        } = this.props;

        if (getApiError) {
            // TODO: error route
            console.log('API Error Response: ', getApiErrorResponse);

            if (getApiErrorResponse && getApiErrorResponse.message) {
                console.log(
                    `API Error Message: Bad request: ${getApiErrorResponse.message}`
                );
            }
        }

        const isStaging =
            window.location.hostname === 'partner.staging.samaritan.city';

        const HomeComponent = this.getHomeComponent();

        return (
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={customTheme}>
                    <AppErrorBoundary>
                        <Router>
                            <Helmet
                                defaultTitle="Samaritan Partner"
                                titleTemplate="%s | Samaritan Partner"
                            >
                                {isStaging && (
                                    <meta
                                        name="robots"
                                        content="noindex, nofollow"
                                    />
                                )}
                            </Helmet>
                            <Switch>
                                <Route
                                    exact
                                    path={Routes.login}
                                    component={Login}
                                />
                                <Route
                                    exact
                                    path={Routes.error}
                                    component={ErrorPage}
                                />
                                <Route
                                    exact
                                    path={Routes.photo}
                                    component={MobilePhotoUpload}
                                />
                                <Route
                                    path={Routes.quick_link}
                                    component={QuickLink}
                                />
                                <PrivateRoute
                                    exact
                                    path={Routes.root}
                                    component={HomeComponent}
                                    unauthorizedUser={unauthorizedUser}
                                    email={email}
                                    name={name}
                                    last_name={last_name}
                                />
                                <PrivateRoute
                                    exact
                                    path={Routes.holder_list}
                                    component={HomeComponent}
                                    unauthorizedUser={unauthorizedUser}
                                    email={email}
                                    name={name}
                                    last_name={last_name}
                                />
                                <PrivateRoute
                                    path={Routes.holder_profile}
                                    component={HolderProfile}
                                    unauthorizedUser={unauthorizedUser}
                                    email={email}
                                    name={name}
                                    last_name={last_name}
                                />
                                <PrivateRoute
                                    exact
                                    path={Routes.intake}
                                    component={Intake}
                                    unauthorizedUser={unauthorizedUser}
                                    email={email}
                                    name={name}
                                    last_name={last_name}
                                />
                                <PrivateRoute
                                    exact
                                    path={Routes.referrals}
                                    component={Referrals}
                                    unauthorizedUser={unauthorizedUser}
                                    email={email}
                                    name={name}
                                    last_name={last_name}
                                />
                                <PrivateRoute
                                    exact
                                    path={Routes.settings}
                                    component={Settings}
                                    unauthorizedUser={unauthorizedUser}
                                    email={email}
                                    name={name}
                                    last_name={last_name}
                                />
                                <LogoutRoute />
                                <Route component={FourOhFour} />
                            </Switch>
                        </Router>
                    </AppErrorBoundary>
                </ThemeProvider>
            </StyledEngineProvider>
        );
    }
}

// TODO: Fix App type as any
export default withCookies(App as any);
